// @ts-strict-ignore
import type { ComputedRef, SetupContext } from 'vue'
import type {
  IToolingListComposable,
  IToolingListComposableComputed,
  IToolingListComposableProps,
  IToolingTableDataElement,
} from '../../../composables'

import type { Equalize } from '../../../modules/equalize'
import { computed, getCurrentInstance } from 'vue'
import { computedFn as toolingListComputedFn } from '../../../composables/use-tooling-list/computed'

import { sortValuesAlphabetically } from '../../../composables/use-tooling-list/utils'

interface IEqualizeViewDataMethodsProps {
  toolingList: IToolingListComposable<Equalize>
}

type IEqualizeListComputed = IToolingListComposableComputed<Equalize> & {
  defaultRows: ComputedRef<Array<IToolingTableDataElement>>
  tableData: ComputedRef<Array<IToolingTableDataElement>>
  excludedRowsIds: ComputedRef<Array<string>>
}

export const computedFn = (
  props: IToolingListComposableProps,
  ctx: SetupContext,
  { toolingList }: IEqualizeViewDataMethodsProps,
): IEqualizeListComputed => {
  const {
    proxy: { $epb },
  } = getCurrentInstance()

  const { pagination } = toolingList

  const toolingListComputed = toolingListComputedFn<Equalize>()
  const { tableData: toolingTableData } = toolingListComputed

  const defaultRows = computed((): Array<IToolingTableDataElement> => {
    const filtersAllMessage = $epb.messages.filters.values.all.value
    const visiblePivotsConfigValue = toolingListComputed.visiblePivotsConfig.value
    const resultPivots = Object.keys(visiblePivotsConfigValue).reduce(
      ({ pivots, pivotsTooltip }, configKey) => {
        pivots[configKey] = filtersAllMessage
        pivotsTooltip[configKey] = sortValuesAlphabetically(
          visiblePivotsConfigValue[configKey].values.map(({ id, prettyName }) => prettyName ?? id),
        )
        return {
          pivots,
          pivotsTooltip,
        }
      },
      {
        pivots: {},
        pivotsTooltip: {},
      },
    )

    const defaultConfigs = toolingListComputed.toolConfig.value.allDefaultEndpoints || []

    return defaultConfigs.map(defaultValue => ({
      ...defaultValue,
      criteria: {
        ...defaultValue?.criteria,
        ...resultPivots,
      },
    }))
  })

  const tableData = computed((): Array<IToolingTableDataElement> => {
    const defaultRowIds = defaultRows.value.map(row => row.id)

    const filteredRegularData = toolingTableData.value.filter(
      item => !defaultRowIds.includes(item.id),
    )

    if (pagination.value.page === 1) {
      return [...defaultRows.value, ...filteredRegularData]
    } else {
      return filteredRegularData
    }
  })

  const excludedRowsIds = computed((): Array<string> => {
    return pagination.value.page === 1 ? defaultRows.value.map(row => row.id) : []
  })

  return {
    ...toolingListComputed,
    defaultRows,
    tableData,
    excludedRowsIds,
  }
}
